import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from './config';
import './ReportBug.css';

const initialForm = {
  title: '',
  severity: 'medium',
  summary: '',
  steps_to_reproduce: '',
  expected_behavior: '',
  actual_behavior: '',
  environment: '',
  attachments_or_logs: '',
  suggested_fix: '',
};

const ReportBug = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reportId, setReportId] = useState(null);

  const previewText = useMemo(() => {
    return [
      `Title: ${form.title || '(missing)'}`,
      `Severity: ${form.severity}`,
      '',
      'Summary:',
      form.summary || '(missing)',
      '',
      'Steps To Reproduce:',
      form.steps_to_reproduce || '(missing)',
      '',
      'Expected Behavior:',
      form.expected_behavior || '(missing)',
      '',
      'Actual Behavior:',
      form.actual_behavior || '(missing)',
      '',
      'Environment:',
      form.environment || '(missing)',
      '',
      'Attachments/Logs:',
      form.attachments_or_logs || '(none)',
      '',
      'Suggested Fix:',
      form.suggested_fix || '(none)',
    ].join('\n');
  }, [form]);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await apiClient.post('/api/feedback/bug-report', form);
      if (response.data?.success) {
        setSuccess(response.data.message || 'Report submitted successfully.');
        setReportId(response.data.report_id || null);
        setForm(initialForm);
      } else {
        setError(response.data?.error || 'Failed to submit report.');
      }
    } catch (err) {
      const message = err?.response?.data?.error || 'Failed to submit report.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="report-bug-page">
      <div className="report-bug-container">
      <div className="report-bug-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back to Home
        </button>
        <h1>Beta Bug Report + Feedback</h1>
        <p>
          High-quality reports help us ship faster. Approved reports get permanent credit-limit boosts by severity.
        </p>
      </div>

      <div className="report-bug-checklist">
        <h3>What makes a report likely to be approved?</h3>
        <ul>
          <li>Describe the issue impact clearly and keep title specific.</li>
          <li>Provide exact, numbered steps so we can reproduce quickly.</li>
          <li>Show expected vs actual behavior and include environment details.</li>
          <li>Attach logs or error snippets when possible.</li>
          <li>Suggest a possible fix if you can.</li>
        </ul>
      </div>

      <form className="report-bug-form" onSubmit={handleSubmit}>
        <label>
          Bug Title
          <input
            type="text"
            value={form.title}
            onChange={(e) => updateField('title', e.target.value)}
            required
            minLength={8}
            placeholder="Example: Auto-apply crashes when job URL contains query params"
          />
        </label>

        <label>
          Suggested Severity
          <select
            value={form.severity}
            onChange={(e) => updateField('severity', e.target.value)}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </label>

        <label>
          Summary
          <textarea
            value={form.summary}
            onChange={(e) => updateField('summary', e.target.value)}
            required
            minLength={20}
            rows={4}
            placeholder="One paragraph summary of the bug and impact."
          />
        </label>

        <label>
          Steps To Reproduce
          <textarea
            value={form.steps_to_reproduce}
            onChange={(e) => updateField('steps_to_reproduce', e.target.value)}
            required
            minLength={20}
            rows={6}
            placeholder="1) Go to ... 2) Click ... 3) Observe ..."
          />
        </label>

        <label>
          Expected Behavior
          <textarea
            value={form.expected_behavior}
            onChange={(e) => updateField('expected_behavior', e.target.value)}
            required
            minLength={8}
            rows={3}
          />
        </label>

        <label>
          Actual Behavior
          <textarea
            value={form.actual_behavior}
            onChange={(e) => updateField('actual_behavior', e.target.value)}
            required
            minLength={8}
            rows={3}
          />
        </label>

        <label>
          Environment
          <textarea
            value={form.environment}
            onChange={(e) => updateField('environment', e.target.value)}
            required
            minLength={8}
            rows={3}
            placeholder="OS, browser/CLI version, app version, account type."
          />
        </label>

        <label>
          Attachments Or Logs (Optional)
          <textarea
            value={form.attachments_or_logs}
            onChange={(e) => updateField('attachments_or_logs', e.target.value)}
            rows={3}
            placeholder="Paste error logs, stack trace, screenshot links, etc."
          />
        </label>

        <label>
          Suggested Fix (Optional)
          <textarea
            value={form.suggested_fix}
            onChange={(e) => updateField('suggested_fix', e.target.value)}
            rows={3}
            placeholder="If you have an idea for a fix, include it here."
          />
        </label>

        {error && <div className="report-bug-message report-bug-error">{error}</div>}
        {success && (
          <div className="report-bug-message report-bug-success">
            {success} {reportId ? `Report ID: #${reportId}` : ''}
          </div>
        )}

        <button type="submit" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit Bug Report'}
        </button>
      </form>

        <div className="report-bug-preview">
          <h3>Submission Preview</h3>
          <pre>{previewText}</pre>
        </div>
      </div>
    </div>
  );
};

export default ReportBug;
